import { Add, DeleteOutline, HexagonOutlined } from "@mui/icons-material";
import {
	Button,
	Card,
	CardContent,
	Chip,
	Grid2,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableRow,
	Typography,
	Box,
	LinearProgress,
} from "@mui/material";
import { Fragment } from "react/jsx-runtime";
import MobileView from "./MobileView";
import StandardView from "./StandardView";
import { OwnerAdjustment } from "../interfaces/AppDefinitions";
import React, { useContext, useState } from "react";
import { AppContext } from "../context/AppContext";
import { NewAdjustment } from "./NewAdjustment";
import { formatCmpctNumber } from "../helpers/utils";
import { AdjustmentIcon } from "../helpers/adjustmentIcon";
import { OwnerConsoleViewController } from "../controllers/OwnerConsoleViewController";
interface Column {
	id: "adjustment_type" | "adjustment_value" | "actionBtn";
	label: string;
	minWidth?: number;
	maxWidth?: number;
	align?: "right" | "center";
	format?: (value: number) => string;
	icon?: React.ReactNode;
}

const columns: readonly Column[] = [
	{ id: "adjustment_type", label: "Adjustment Type", minWidth: 100, icon: <HexagonOutlined fontSize="small" /> },
	{
		id: "adjustment_value",
		label: "($) Cost",
		maxWidth: 50,
		format: (value: number) => `${formatCmpctNumber(value)}`,
	},
	{
		id: "actionBtn",
		label: "",
		align: "center",
		icon: <DeleteOutline fontSize="small" sx={{ pointerEvents: "all" }} />,
	},
];

export default function OwnerAdjustments(props: { mobileView?: boolean }) {
	const { appState, appDispatch } = useContext(AppContext);
	const [showModal, toggleModal] = useState(false);
	// Where the button was tapped, so the form opens right there (the report can be a tall iframe).
	const [anchor, setAnchor] = useState<DOMRect | null>(null);

	const { report_configuration, currentAddrId, isFetchingAdjustments } = appState;
	const { owner_adjustments } = report_configuration.find((x) => x.addrId === currentAddrId) ?? {};
	async function handleDeleteAdjustment(adjustment: OwnerAdjustment) {
		try {
			var proceed = window.confirm("Are you sure you want to delete this adjustment?");
			if (proceed) {
				appDispatch({ type: "SET_FETCHING_ADJUSTMENTS", value: true });

				await OwnerConsoleViewController.DeleteAdjustment({
					adjustment_type_id: adjustment.adjustment_type_id,
					addrId: adjustment.addrid,
					enc: appState.enc,
					track: appState.trackView,
				});
				const { report_configuration } = await OwnerConsoleViewController.GetReportConfiguration(appState.enc);
				if (report_configuration) {
					appDispatch({ type: "UPDATE_STATE", key: "report_configuration", value: report_configuration });
				}
			}
		} finally {
			appDispatch({ type: "SET_FETCHING_ADJUSTMENTS", value: false });
		}
	}
	function RenderColumn(column: Column, value: any, row: OwnerAdjustment) {
		if (column.id === "actionBtn" || column.id === "adjustment_type") {
			return (
				<Typography component={"div"}>
					<Chip
						onClick={async () => {
							if (column.id === "actionBtn") {
								await handleDeleteAdjustment(row);
							}
						}}
						sx={{ border: "none" }}
						icon={column.id === "adjustment_type" ? GetAdjustmentColumnIcon(row) : <> {column.icon}</>}
						variant="outlined"
						label={column.format && typeof value === "number" ? column.format(value) : value}
					/>
				</Typography>
			);
		}
		if (column.format) {
			return column.format(value);
		}
		return value;
	}
	function GetAdjustmentColumnIcon(row: OwnerAdjustment) {
		return <AdjustmentIcon type={row.adjustment_type} fontSize="small" />;
	}
	var content = (
		<Card>
			<CardContent>
				<NewAdjustment show={showModal} toggleModal={toggleModal} anchor={anchor} />
				<Grid2 container spacing={0}>
					<Grid2 size={8}>
						<Typography variant="h6" textAlign={"left"} pl={2}>
							Adjustments
						</Typography>
					</Grid2>
					{isFetchingAdjustments && (
						<Grid2 size={12}>
							<Box sx={{ width: "100%" }}>
								<LinearProgress />
								<Typography sx={{ textAlign: "center" }}>Retrieving Adjustments...</Typography>
							</Box>
						</Grid2>
					)}
					{!isFetchingAdjustments && (
						<Fragment>
							<Grid2 size={4} sx={{ textAlign: "right" }}>
								<Button
									onClick={(e) => {
										setAnchor(e.currentTarget.getBoundingClientRect());
										toggleModal(true);
									}}
									variant="outlined"
									startIcon={<Add sx={{ color: appState.colorPallete["scheme3"] }} />}
									sx={{
										borderRadius: "8px",
										borderColor: appState.colorPallete["scheme3"],
										color: appState.colorPallete["scheme3"],
									}}
								>
									Add
								</Button>
							</Grid2>
							<Grid2 size={12}>
								{owner_adjustments && owner_adjustments.length === 0 && (
									<Typography sx={{ textAlign: "center" }}> No Adjustment yet</Typography>
								)}
								{owner_adjustments && owner_adjustments.length > 0 && (
									<TableContainer sx={{ maxHeight: 440 }}>
										<Table stickyHeader aria-label="sticky table">
											{/* <TableHead>
												<TableRow>
													{columns.map((column) => (
														<TableCell
															key={column.id}
															align={column.align}
															style={{
																minWidth: column.minWidth,
																maxWidth: column.maxWidth,
																backgroundColor: APP_COLOR_SCHEME["200"],
															}}
														>
															{column.label}
														</TableCell>
													))}
												</TableRow>
											</TableHead> */}
											<TableBody>
												{owner_adjustments?.map((row) => {
													return (
														<TableRow
															hover
															role="checkbox"
															tabIndex={-1}
															key={row.adjustment_type_id}
														>
															{columns.map((column) => {
																const value = row[column.id as keyof OwnerAdjustment];
																return (
																	<TableCell
																		size="small"
																		key={column.id}
																		align={column.align}
																		sx={{
																			pointerEvents: "none",
																		}}
																	>
																		{RenderColumn(column, value, row)}
																	</TableCell>
																);
															})}
														</TableRow>
													);
												})}
											</TableBody>
										</Table>
									</TableContainer>
								)}
							</Grid2>
						</Fragment>
					)}
				</Grid2>
			</CardContent>
		</Card>
	);
	if (props.mobileView) {
		return <MobileView>{content}</MobileView>;
	}
	return <StandardView> {content}</StandardView>;
}
